import React, { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useLanguage } from '../../contexts/LanguageContext';
import { supabaseAuth } from '../../services/supabaseAuth';
import type { SupabaseUser } from '../../services/supabaseAuth';
import { loadSubscriptionFromSupabase, type SubscriptionRecord } from '../../services/subscriptionSyncService';
import { signOutCompletely, cancelSubscriptionAtPeriodEnd, deleteAccountPermanently } from '../../services/accountSessionService';
import { appSubscriptionTierFromStored, type SubscriptionTier } from '../../utils/subscriptionTier';
import './LegalPage.css';

type Lang = 'en' | 'nl' | 'ar';

const TEXTS: Record<
  Lang,
  {
    title: string;
    effective: string;
    sectionManage: string;
    displayName: string;
    signedInAs: string;
    provider: string;
    providerGoogle: string;
    providerApple: string;
    providerFacebook: string;
    providerEmail: string;
    unknownEmail: string;
    sectionSubscription: string;
    tierFree: string;
    tierWeekly: string;
    tierMonthly: string;
    tierLifetime: string;
    statusActive: string;
    renews: string;
    cancelAtEnd: string;
    cancelSubscription: string;
    cancelSuccess: string;
    cancelError: string;
    noStripeId: string;
    upgradeCta: string;
    lifetimeNoCancel: string;
    sectionDanger: string;
    deleteCta: string;
    deleteHint: string;
    sectionSignOut: string;
    signOutHint: string;
    signOut: string;
    emailManagedOAuth: string;
    changeEmail: string;
    newEmailLabel: string;
    saveEmail: string;
    emailCheckInbox: string;
    emailError: string;
    footerIntro: string;
    privacyLink: string;
    termsLink: string;
    genericCancel: string;
    loading: string;
    notSignedIn: string;
    goHome: string;
    confirmSignOutTitle: string;
    confirmSignOutBody: string;
    confirmSignOutBack: string;
    confirmSignOutOk: string;
    confirmCancelTitle: string;
    confirmCancelBody: string;
    confirmCancelKeep: string;
    confirmCancelOk: string;
    confirmDeleteTitle: string;
    confirmDeleteBody: string;
    confirmDeleteBack: string;
    confirmDeleteOk: string;
    deleteError: string;
    cancelReasonTitle: string;
    cancelReasonBody: string;
    cancelReasonExpensive: string;
    cancelReasonUnused: string;
    cancelReasonPassed: string;
    cancelReasonOther: string;
    cancelReasonPlaceholder: string;
    cancelReasonContinue: string;
    cancelReasonBack: string;
    upgradePlanCta: string;
    tierPrice_weekly: string;
    tierPrice_monthly: string;
    tierPrice_lifetime: string;
    tierDesc_free: string;
    accessUntil: string;
    cancelsSoon: string;
  }
> = {
  en: {
    title: 'Account',
    effective: 'Updated Apr 2026',
    sectionManage: 'Manage account',
    displayName: 'Name',
    signedInAs: 'Email',
    provider: 'Signed in with',
    providerGoogle: 'Google',
    providerApple: 'Apple',
    providerFacebook: 'Facebook',
    providerEmail: 'Email',
    unknownEmail: '—',
    sectionSubscription: 'Subscription',
    tierFree: 'Free',
    tierWeekly: 'Pro · Weekly',
    tierMonthly: 'Pro · Monthly',
    tierLifetime: 'Lifetime',
    statusActive: 'Active',
    renews: 'Renews or ends',
    cancelAtEnd: 'Cancellation scheduled: access until period end',
    cancelSubscription: 'Cancel subscription',
    cancelSuccess: 'Your subscription will end after the current billing period. You can keep using Pro until then.',
    cancelError: 'Could not cancel. Try again or contact support.',
    noStripeId: 'Subscription details are incomplete. Use Support in Settings (Ideas & Bugs) if you need help.',
    upgradeCta: 'Upgrade',
    lifetimeNoCancel: 'Lifetime has no renewal to cancel.',
    sectionDanger: 'Account deletion',
    deleteCta: 'Delete account',
    deleteHint: 'Permanently remove your account and associated data from TheoryCoach. This happens only if you confirm below.',
    sectionSignOut: 'Sign out',
    signOutHint: 'On shared devices, sign out when you are done.',
    signOut: 'Sign out',
    emailManagedOAuth: 'Your sign-in email is managed by {provider}. It cannot be changed here.',
    changeEmail: 'Change email',
    newEmailLabel: 'New email',
    saveEmail: 'Send confirmation link',
    emailCheckInbox: 'We sent a confirmation link. Open the email at your new address to finish the update.',
    emailError: 'Could not start email change. Check the address and try again.',
    footerIntro: 'Account actions are provided by TheoryCoach.',
    privacyLink: 'Privacy Policy',
    termsLink: 'Terms of Service',
    genericCancel: 'Cancel',
    loading: 'Loading…',
    notSignedIn: 'You are not signed in.',
    goHome: 'Back to home',
    confirmSignOutTitle: 'Sign out?',
    confirmSignOutBody: 'You will need to sign in again to use your account on this device.',
    confirmSignOutBack: 'Stay signed in',
    confirmSignOutOk: 'Sign out',
    confirmCancelTitle: 'Cancel subscription?',
    confirmCancelBody: 'You keep access until the end of the current period. Auto-renew will stop after that.',
    confirmCancelKeep: 'Keep subscription',
    confirmCancelOk: 'Cancel renewal',
    confirmDeleteTitle: 'Delete your account?',
    confirmDeleteBody: 'This cannot be undone. Your profile, progress, and subscription data will be permanently removed.',
    confirmDeleteBack: 'Cancel',
    confirmDeleteOk: 'Delete account',
    deleteError: 'Could not delete your account. Try again or contact support.',
    cancelReasonTitle: 'Help us improve',
    cancelReasonBody: 'Why are you cancelling?',
    cancelReasonExpensive: 'Too expensive',
    cancelReasonUnused: "I don't use it enough",
    cancelReasonPassed: 'I passed my CBR theory exam',
    cancelReasonOther: 'Other',
    cancelReasonPlaceholder: 'Tell us more (optional)',
    cancelReasonContinue: 'Continue',
    cancelReasonBack: 'Back',
    upgradePlanCta: 'Upgrade plan',
    tierPrice_weekly: '€8 / week',
    tierPrice_monthly: '€20 / month',
    tierPrice_lifetime: 'One-time · No renewal',
    tierDesc_free: 'Limited access · 3 topics',
    accessUntil: 'Access until',
    cancelsSoon: 'Cancels soon',
  },
  nl: {
    title: 'Account',
    effective: 'Bijgewerkt apr 2026',
    sectionManage: 'Account beheren',
    displayName: 'Naam',
    signedInAs: 'E-mail',
    provider: 'Inloggen met',
    providerGoogle: 'Google',
    providerApple: 'Apple',
    providerFacebook: 'Facebook',
    providerEmail: 'E-mail',
    unknownEmail: '—',
    sectionSubscription: 'Abonnement',
    tierFree: 'Gratis',
    tierWeekly: 'Pro · Wekelijks',
    tierMonthly: 'Pro · Maandelijks',
    tierLifetime: 'Levenslang',
    statusActive: 'Actief',
    renews: 'Verlengt of eindigt',
    cancelAtEnd: 'Opzegging gepland: toegang tot periode-einde',
    cancelSubscription: 'Opzeggen',
    cancelSuccess: 'Je abonnement stopt na de lopende periode. Je behoudt Pro tot die tijd.',
    cancelError: 'Opzeggen mislukt. Probeer opnieuw of neem contact op.',
    noStripeId: 'Abonnementsgegevens zijn onvolledig. Gebruik Support (Ideeën & bugs) in Instellingen.',
    upgradeCta: 'Upgrade naar Pro',
    lifetimeNoCancel: 'Levenslang: geen verlenging om op te zeggen.',
    sectionDanger: 'Account verwijderen',
    deleteCta: 'Account verwijderen',
    deleteHint: 'Verwijdert je account en bijbehorende gegevens permanent. Alleen na bevestiging.',
    sectionSignOut: 'Uitloggen',
    signOutHint: 'Op gedeelde apparaten: uitloggen als je klaar bent.',
    signOut: 'Uitloggen',
    emailManagedOAuth: 'Je inlog-e-mail wordt beheerd door {provider}. Die kun je hier niet wijzigen.',
    changeEmail: 'E-mail wijzigen',
    newEmailLabel: 'Nieuw e-mailadres',
    saveEmail: 'Bevestigingslink verzenden',
    emailCheckInbox: 'We stuurden een bevestigingslink. Open de e-mail op het nieuwe adres om te voltooien.',
    emailError: 'E-mailwijziging starten mislukt. Controleer het adres en probeer opnieuw.',
    footerIntro: 'Accountacties door TheoryCoach.',
    privacyLink: 'Privacybeleid',
    termsLink: 'Voorwaarden',
    genericCancel: 'Annuleren',
    loading: 'Laden…',
    notSignedIn: 'Je bent niet ingelogd.',
    goHome: 'Naar start',
    confirmSignOutTitle: 'Uitloggen?',
    confirmSignOutBody: 'Je moet opnieuw inloggen om je account te gebruiken.',
    confirmSignOutBack: 'Blijf ingelogd',
    confirmSignOutOk: 'Uitloggen',
    confirmCancelTitle: 'Abonnement opzeggen?',
    confirmCancelBody: 'Je houdt toegang tot het einde van de lopende periode. Verlenging stopt daarna.',
    confirmCancelKeep: 'Behoud abonnement',
    confirmCancelOk: 'Beëindig verlenging',
    confirmDeleteTitle: 'Account verwijderen?',
    confirmDeleteBody: 'Dit kan niet ongedaan worden gemaakt. Profiel, voortgang en abonnementsgegevens worden permanent gewist.',
    confirmDeleteBack: 'Annuleren',
    confirmDeleteOk: 'Verwijder account',
    deleteError: 'Verwijderen mislukt. Probeer opnieuw of neem contact op.',
    cancelReasonTitle: 'Help ons verbeteren',
    cancelReasonBody: 'Waarom zeg je op?',
    cancelReasonExpensive: 'Te duur',
    cancelReasonUnused: 'Ik gebruik het te weinig',
    cancelReasonPassed: 'Ik ben geslaagd voor mijn CBR theorie',
    cancelReasonOther: 'Anders',
    cancelReasonPlaceholder: 'Vertel ons meer (optioneel)',
    cancelReasonContinue: 'Doorgaan',
    cancelReasonBack: 'Terug',
    upgradePlanCta: 'Upgrade abonnement',
    tierPrice_weekly: '€8 / week',
    tierPrice_monthly: '€20 / maand',
    tierPrice_lifetime: 'Eenmalig · Geen verlenging',
    tierDesc_free: 'Beperkte toegang · 3 onderwerpen',
    accessUntil: 'Toegang tot',
    cancelsSoon: 'Loopt binnenkort af',
  },
  ar: {
    title: 'الحساب',
    effective: 'آبريل 2026',
    sectionManage: 'إدارة الحساب',
    displayName: 'الاسم',
    signedInAs: 'البريد',
    provider: 'تسجيل الدخول عبر',
    providerGoogle: 'Google',
    providerApple: 'Apple',
    providerFacebook: 'Facebook',
    providerEmail: 'البريد',
    unknownEmail: '—',
    sectionSubscription: 'الاشتراك',
    tierFree: 'مجاني',
    tierWeekly: 'Pro · أسبوعي',
    tierMonthly: 'Pro · شهري',
    tierLifetime: 'مدى الحياة',
    statusActive: 'نشط',
    renews: 'يتجدد أو ينتهي',
    cancelAtEnd: 'إلغاء مجدول: وصول حتى نهاية الفترة',
    cancelSubscription: 'إلغاء الاشتراك',
    cancelSuccess: 'سينتهي اشتراكك بعد الفترة الحالية. ستحتفظ بميزات Pro حتى ذلك الحين.',
    cancelError: 'تعذر الإلغاء. حاول مرة أخرى أو تواصل مع الدعم.',
    noStripeId: 'تفاصيل الاشتراك ناقصة. استخدم الدعم من الإعدادات.',
    upgradeCta: 'الترقية إلى Pro',
    lifetimeNoCancel: 'مدى الحياة: لا يوجد تجديد لإلغائه.',
    sectionDanger: 'حذف الحساب',
    deleteCta: 'حذف الحساب',
    deleteHint: 'يُزال حسابك وجميع البيانات المرتبطة نهائياً بعد التأكيد هنا فقط.',
    sectionSignOut: 'تسجيل الخروج',
    signOutHint: 'على الأجهزة المشتركة، سجّل الخروج عند الانتهاء.',
    signOut: 'تسجيل الخروج',
    emailManagedOAuth: 'البريد المسؤول عنه {provider} لا يُغيّر من هنا.',
    changeEmail: 'تغيير البريد',
    newEmailLabel: 'بريد جديد',
    saveEmail: 'إرسال رابط التأكيد',
    emailCheckInbox: 'أرسلنا رابط تأكيد. راجع بريدك الجديد لإكمال التحديث.',
    emailError: 'تعذر بدء تغيير البريد. تحقق من العنوان وحاول مرة أخرى.',
    footerIntro: 'الإجراءات من TheoryCoach.',
    privacyLink: 'سياسة الخصوصية',
    termsLink: 'الشروط',
    genericCancel: 'إلغاء',
    loading: 'جارٍ التحميل…',
    notSignedIn: 'لم تسجّل الدخول.',
    goHome: 'للرئيسية',
    confirmSignOutTitle: 'تسجيل الخروج؟',
    confirmSignOutBody: 'ستحتاج إلى تسجيل الدخول مرة أخرى.',
    confirmSignOutBack: 'البقاء',
    confirmSignOutOk: 'خروج',
    confirmCancelTitle: 'إلغاء الاشتراك؟',
    confirmCancelBody: 'تبقى لديك الصلاحية حتى نهاية الفترة الحالية. يتوقف التجديد بعدها.',
    confirmCancelKeep: 'الإبقاء',
    confirmCancelOk: 'إلغاء التجديد',
    confirmDeleteTitle: 'حذف الحساب؟',
    confirmDeleteBody: 'لا يمكن التراجع. سيتم حذف الملف والتقدم وبيانات الاشتراك نهائياً.',
    confirmDeleteBack: 'إلغاء',
    confirmDeleteOk: 'حذف نهائياً',
    deleteError: 'تعذر حذف الحساب. حاول مرة أخرى أو تواصل مع الدعم.',
    cancelReasonTitle: 'ساعدنا على التحسين',
    cancelReasonBody: 'لماذا تلغي اشتراكك؟',
    cancelReasonExpensive: 'مكلف جداً',
    cancelReasonUnused: 'لا أستخدمه بما يكفي',
    cancelReasonPassed: 'اجتزت اختبار النظرية CBR',
    cancelReasonOther: 'أخرى',
    cancelReasonPlaceholder: 'أخبرنا بالمزيد (اختياري)',
    cancelReasonContinue: 'متابعة',
    cancelReasonBack: 'رجوع',
    upgradePlanCta: 'ترقية الخطة',
    tierPrice_weekly: '€8 / أسبوع',
    tierPrice_monthly: '€20 / شهر',
    tierPrice_lifetime: 'مرة واحدة · بلا تجديد',
    tierDesc_free: 'وصول محدود · 3 موضوعات',
    accessUntil: 'الوصول حتى',
    cancelsSoon: 'ينتهي قريباً',
  },
};

function tierLabel(tier: SubscriptionTier, tx: (typeof TEXTS)['en']): string {
  if (tier === 'weekly') return tx.tierWeekly;
  if (tier === 'monthly') return tx.tierMonthly;
  if (tier === 'lifetime') return tx.tierLifetime;
  return tx.tierFree;
}

function tierPrice(tier: SubscriptionTier, tx: (typeof TEXTS)['en']): string {
  if (tier === 'weekly') return tx.tierPrice_weekly;
  if (tier === 'monthly') return tx.tierPrice_monthly;
  if (tier === 'lifetime') return tx.tierPrice_lifetime;
  return tx.tierDesc_free;
}

function providerLabel(p: SupabaseUser['provider'], tx: (typeof TEXTS)['en']): string {
  if (p === 'google') return tx.providerGoogle;
  if (p === 'apple') return tx.providerApple;
  if (p === 'facebook') return tx.providerFacebook;
  return tx.providerEmail;
}

function formatDate(iso: string | null, locale: string): string | null {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleDateString(locale, { day: 'numeric', month: 'short', year: 'numeric' });
}

export default function AccountPage() {
  const navigate = useNavigate();
  const { t_nested, currentLanguage } = useLanguage();
  const lang: Lang = currentLanguage === 'nl' || currentLanguage === 'ar' ? currentLanguage : 'en';
  const tx = TEXTS[lang];
  const locale = lang === 'nl' ? 'nl-NL' : lang === 'ar' ? 'ar' : 'en-GB';
  const dir = lang === 'ar' ? 'rtl' : 'ltr';

  const [user, setUser] = useState<SupabaseUser | null>(null);
  const [subRecord, setSubRecord] = useState<SubscriptionRecord | null>(null);
  const [loading, setLoading] = useState(true);
  const [signOutOpen, setSignOutOpen] = useState(false);
  const [cancelOpen, setCancelOpen] = useState(false);
  const [signOutWorking, setSignOutWorking] = useState(false);
  const [cancelWorking, setCancelWorking] = useState(false);
  const [inlineError, setInlineError] = useState<string | null>(null);
  const [inlineSuccess, setInlineSuccess] = useState<string | null>(null);
  const [cancelDone, setCancelDone] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleteWorking, setDeleteWorking] = useState(false);
  const [emailFormOpen, setEmailFormOpen] = useState(false);
  const [newEmailValue, setNewEmailValue] = useState('');
  const [emailWorking, setEmailWorking] = useState(false);
  const [cancelStep, setCancelStep] = useState<1 | 2>(1);
  const [cancelReason, setCancelReason] = useState('');
  const [cancelReasonOther, setCancelReasonOther] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setInlineError(null);
    try {
      const u = await supabaseAuth.getCurrentUser();
      setUser(u);
      if (u) {
        const row = await loadSubscriptionFromSupabase(u.id);
        setSubRecord(row);
      } else {
        setSubRecord(null);
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    const anyModal = signOutOpen || cancelOpen || deleteOpen;
    if (!anyModal) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      if (deleteWorking || cancelWorking || signOutWorking) return;
      if (deleteOpen) setDeleteOpen(false);
      else if (cancelOpen) { if (cancelStep === 2) { setCancelStep(1); } else { setCancelOpen(false); } }
      else if (signOutOpen) setSignOutOpen(false);
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [signOutOpen, cancelOpen, cancelStep, deleteOpen, deleteWorking, cancelWorking, signOutWorking]);

  const appTier: SubscriptionTier = subRecord
    ? appSubscriptionTierFromStored(subRecord.tier)
    : 'free';
  const canCancelRecurring =
    !!subRecord?.stripe_subscription_id &&
    (appTier === 'weekly' || appTier === 'monthly') &&
    subRecord.is_active;

  const onConfirmSignOut = async () => {
    setSignOutWorking(true);
    try {
      await signOutCompletely();
    } finally {
      setSignOutWorking(false);
    }
  };

  const onConfirmDeleteAccount = async () => {
    setDeleteWorking(true);
    setInlineError(null);
    const { success, error } = await deleteAccountPermanently();
    if (success) {
      setDeleteOpen(false);
      await signOutCompletely();
    } else {
      setDeleteWorking(false);
      setInlineError(error || tx.deleteError);
    }
  };

  const onConfirmCancel = async () => {
    if (!user || !subRecord?.stripe_subscription_id) return;
    setCancelWorking(true);
    setInlineError(null);
    const resolvedReason = cancelReason === 'other' ? cancelReasonOther : cancelReason;
    const { success, error } = await cancelSubscriptionAtPeriodEnd(user.id, subRecord.stripe_subscription_id, resolvedReason || undefined);
    setCancelWorking(false);
    if (success) {
      setCancelOpen(false);
      setCancelDone(true);
      setInlineSuccess(tx.cancelSuccess);
      const row = await loadSubscriptionFromSupabase(user.id);
      setSubRecord(row);
    } else {
      setInlineError(error || tx.cancelError);
    }
  };

  if (loading) {
    return (
      <div className="legal-page" dir={dir}>
        <div className="legal-page-shell">
          <p className="legal-intro">{tx.loading}</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="legal-page" dir={dir}>
        <div className="legal-page-shell">
          <p className="legal-intro">{tx.notSignedIn}</p>
          <Link to="/dashboard" className="legal-btn legal-btn-primary" style={{ display: 'inline-block', textDecoration: 'none' }}>
            {tx.goHome}
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="legal-page" dir={dir}>
      <div className="legal-page-shell account-page">
        <div className="legal-page-header">
          <div className="legal-page-header-left">
            <button type="button" className="legal-page-back" onClick={() => navigate('/dashboard')} aria-label={t_nested('aria.goBack')}>
              {lang === 'ar' ? 'رجوع →' : lang === 'nl' ? '← Terug' : '← Back'}
            </button>
            <h1 className="legal-page-title">{tx.title}</h1>
          </div>
          <div className="legal-page-meta">
            <span className="legal-chip">TheoryCoach</span>
            <span className="legal-chip">{tx.effective}</span>
          </div>
        </div>

        <div className="legal-page-content">
          {inlineSuccess && <p className="account-flash account-flash--success" role="status">{inlineSuccess}</p>}
          {inlineError && <p className="account-flash account-flash--error" role="alert">{inlineError}</p>}

          <section className="legal-section account-card" aria-labelledby="acc-manage">
            <h2 id="acc-manage">{tx.sectionManage}</h2>
            <p>
              <strong>{tx.displayName}: </strong>
              {user.name || '—'}
            </p>
            <p>
              <strong>{tx.signedInAs}: </strong>
              {user.email || tx.unknownEmail}
            </p>
            <p>
              <strong>{tx.provider}: </strong>
              {providerLabel(user.provider, tx)}
            </p>
            {user.provider !== 'email' ? (
              <p className="account-note">
                {tx.emailManagedOAuth.replace('{provider}', providerLabel(user.provider, tx))}
              </p>
            ) : (
              <div className="account-email-block">
                {!emailFormOpen ? (
                  <div className="legal-action-row">
                    <button
                      type="button"
                      className="legal-btn legal-btn-ghost"
                      onClick={() => {
                        setEmailFormOpen(true);
                        setNewEmailValue(user.email || '');
                        setInlineError(null);
                      }}
                    >
                      {tx.changeEmail}
                    </button>
                  </div>
                ) : (
                  <div className="account-email-form">
                    <label htmlFor="acc-new-email" className="account-email-label">
                      {tx.newEmailLabel}
                    </label>
                    <input
                      id="acc-new-email"
                      type="email"
                      className="account-email-input"
                      value={newEmailValue}
                      onChange={(e) => setNewEmailValue(e.target.value)}
                      autoComplete="email"
                    />
                    <div className="legal-action-row">
                      <button
                        type="button"
                        className="legal-btn legal-btn-ghost"
                        disabled={emailWorking}
                        onClick={() => {
                          setEmailFormOpen(false);
                        }}
                      >
                        {tx.genericCancel}
                      </button>
                      <button
                        type="button"
                        className="legal-btn legal-btn-primary"
                        disabled={emailWorking}
                        onClick={async () => {
                          setEmailWorking(true);
                          setInlineError(null);
                          const { error } = await supabaseAuth.requestEmailChange(newEmailValue);
                          setEmailWorking(false);
                          if (error) {
                            setInlineError(tx.emailError);
                          } else {
                            setEmailFormOpen(false);
                            setInlineSuccess(tx.emailCheckInbox);
                          }
                        }}
                      >
                        {tx.saveEmail}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}
          </section>

          <section className="legal-section account-card" id="subscription" aria-labelledby="acc-sub">
            <h2 id="acc-sub">{tx.sectionSubscription}</h2>

            <div className="account-sub-card">
              <div className="account-sub-top">
                <span className={`account-tier-badge account-tier-badge--${appTier}`}>
                  {tierLabel(appTier, tx)}
                </span>
                {appTier !== 'free' && (
                  <span className={`account-status-pill${cancelDone ? ' account-status-pill--cancelling' : ''}`}>
                    {cancelDone ? tx.cancelsSoon : tx.statusActive}
                  </span>
                )}
              </div>
              <p className="account-sub-price">{tierPrice(appTier, tx)}</p>
              {subRecord?.expires_at && appTier !== 'free' && (
                <p className="account-sub-date">
                  {cancelDone ? tx.accessUntil : tx.renews}: {formatDate(subRecord.expires_at, locale) || '—'}
                </p>
              )}
              {appTier === 'lifetime' && <p className="account-note">{tx.lifetimeNoCancel}</p>}
            </div>

            {appTier === 'free' && (
              <div className="legal-action-row">
                <Link to="/subscription" className="legal-btn legal-btn-primary" style={{ textDecoration: 'none' }}>
                  {tx.upgradeCta}
                </Link>
              </div>
            )}

            {(appTier === 'weekly' || appTier === 'monthly') && subRecord?.is_active && !cancelDone && (
              <div className="legal-action-row account-sub-actions">
                <Link to="/subscription" className="legal-btn legal-btn-ghost" style={{ textDecoration: 'none' }}>
                  {tx.upgradePlanCta}
                </Link>
                {canCancelRecurring && (
                  <button
                    type="button"
                    className="legal-btn legal-btn-danger"
                    onClick={() => { setCancelOpen(true); setCancelStep(1); setCancelReason(''); setCancelReasonOther(''); setInlineError(null); }}
                  >
                    {tx.cancelSubscription}
                  </button>
                )}
              </div>
            )}

            {(appTier === 'weekly' || appTier === 'monthly') && subRecord?.is_active && !subRecord.stripe_subscription_id && (
              <p className="account-note">{tx.noStripeId}</p>
            )}
          </section>

          <section className="legal-section account-card legal-section--danger" aria-labelledby="acc-del">
            <h2 id="acc-del">{tx.sectionDanger}</h2>
            <p>{tx.deleteHint}</p>
            <div className="legal-action-row">
              <button
                type="button"
                className="legal-btn legal-btn-danger"
                onClick={() => { setDeleteOpen(true); setInlineError(null); }}
              >
                {tx.deleteCta}
              </button>
            </div>
          </section>

          <section className="legal-section account-card" aria-labelledby="acc-so">
            <h2 id="acc-so">{tx.sectionSignOut}</h2>
            <p>{tx.signOutHint}</p>
            <div className="legal-action-row">
              <button type="button" className="legal-btn legal-btn-primary" onClick={() => setSignOutOpen(true)}>
                {tx.signOut}
              </button>
            </div>
          </section>

          <p className="legal-disclaimer account-footer-legal">
            {tx.footerIntro}{' '}
            <Link to="/settings/privacy" className="account-footer-link">
              {tx.privacyLink}
            </Link>
            {' · '}
            <Link to="/settings/terms" className="account-footer-link">
              {tx.termsLink}
            </Link>
          </p>
        </div>
      </div>

      {signOutOpen && (
        <div
          className="legal-dialog-backdrop"
          role="presentation"
          onClick={() => !signOutWorking && setSignOutOpen(false)}
        >
          <div
            className="legal-dialog"
            role="alertdialog"
            aria-modal="true"
            aria-labelledby="dlg-signout-title"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 id="dlg-signout-title" className="legal-dialog-title">
              {tx.confirmSignOutTitle}
            </h3>
            <p className="legal-dialog-body">{tx.confirmSignOutBody}</p>
            <div className="legal-dialog-actions">
              <button type="button" className="legal-btn legal-btn-ghost" disabled={signOutWorking} onClick={() => setSignOutOpen(false)}>
                {tx.confirmSignOutBack}
              </button>
              <button type="button" className="legal-btn legal-btn-primary" disabled={signOutWorking} onClick={onConfirmSignOut}>
                {tx.confirmSignOutOk}
              </button>
            </div>
          </div>
        </div>
      )}

      {cancelOpen && (
        <div
          className="legal-dialog-backdrop"
          role="presentation"
          onClick={() => {
            if (cancelWorking) return;
            if (cancelStep === 2) { setCancelStep(1); } else { setCancelOpen(false); }
          }}
        >
          <div
            className="legal-dialog"
            role="alertdialog"
            aria-modal="true"
            aria-labelledby="dlg-cancel-title"
            onClick={(e) => e.stopPropagation()}
          >
            {cancelStep === 1 ? (
              <>
                <h3 id="dlg-cancel-title" className="legal-dialog-title">{tx.cancelReasonTitle}</h3>
                <p className="legal-dialog-body">{tx.cancelReasonBody}</p>
                <div className="cancel-reason-list">
                  {(['expensive', 'unused', 'passed', 'other'] as const).map((key) => {
                    const labelMap = {
                      expensive: tx.cancelReasonExpensive,
                      unused: tx.cancelReasonUnused,
                      passed: tx.cancelReasonPassed,
                      other: tx.cancelReasonOther,
                    };
                    return (
                      <label key={key} className="cancel-reason-item">
                        <input
                          type="radio"
                          name="cancel-reason"
                          value={key}
                          checked={cancelReason === key}
                          onChange={() => setCancelReason(key)}
                        />
                        <span>{labelMap[key]}</span>
                      </label>
                    );
                  })}
                  {cancelReason === 'other' && (
                    <textarea
                      className="cancel-reason-other"
                      placeholder={tx.cancelReasonPlaceholder}
                      value={cancelReasonOther}
                      onChange={(e) => setCancelReasonOther(e.target.value)}
                      rows={2}
                      maxLength={300}
                    />
                  )}
                </div>
                <div className="legal-dialog-actions">
                  <button type="button" className="legal-btn legal-btn-ghost" onClick={() => setCancelOpen(false)}>
                    {tx.cancelReasonBack}
                  </button>
                  <button
                    type="button"
                    className="legal-btn legal-btn-primary"
                    disabled={!cancelReason}
                    onClick={() => setCancelStep(2)}
                  >
                    {tx.cancelReasonContinue}
                  </button>
                </div>
              </>
            ) : (
              <>
                <h3 id="dlg-cancel-title" className="legal-dialog-title">{tx.confirmCancelTitle}</h3>
                <p className="legal-dialog-body">
                  {tx.confirmCancelBody}
                  {subRecord?.expires_at && (
                    <> <strong>{formatDate(subRecord.expires_at, locale)}.</strong></>
                  )}
                </p>
                <div className="legal-dialog-actions">
                  <button type="button" className="legal-btn legal-btn-ghost" disabled={cancelWorking} onClick={() => setCancelStep(1)}>
                    {tx.confirmCancelKeep}
                  </button>
                  <button type="button" className="legal-btn legal-btn-danger" disabled={cancelWorking} onClick={onConfirmCancel}>
                    {tx.confirmCancelOk}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {deleteOpen && (
        <div
          className="legal-dialog-backdrop"
          role="presentation"
          onClick={() => !deleteWorking && setDeleteOpen(false)}
        >
          <div
            className="legal-dialog"
            role="alertdialog"
            aria-modal="true"
            aria-labelledby="dlg-delete-title"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 id="dlg-delete-title" className="legal-dialog-title">
              {tx.confirmDeleteTitle}
            </h3>
            <p className="legal-dialog-body">{tx.confirmDeleteBody}</p>
            <div className="legal-dialog-actions">
              <button type="button" className="legal-btn legal-btn-ghost" disabled={deleteWorking} onClick={() => setDeleteOpen(false)}>
                {tx.confirmDeleteBack}
              </button>
              <button type="button" className="legal-btn legal-btn-danger" disabled={deleteWorking} onClick={onConfirmDeleteAccount}>
                {tx.confirmDeleteOk}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
